import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {PendingAttachment} from 'app/blocks/service/assistant-context/assistant-attachments';

/**
 * Attachments waiting for the next message, per chat session tab (CU-14zefhy1qnh). Lives in the root injector so the
 * pills survive navigating to another screen (to attach it too) and closing / reopening the chat panel. In memory only:
 * a page reload starts with nothing pending.
 *
 * Also the "Ask CoPilot" bridge: error toasts / dialogs call `askAboutError`, the chat (when enabled) opens, attaches
 * the screen and prefills the question.
 */
@Injectable({providedIn: 'root'})
export class AssistantAttachmentsService {
    /** set by the chat from the role setting; error toasts / dialogs show "Ask CoPilot" only when true */
    copilotEnabled = false;
    /** the tenant's date format from the chat state, set by the chat; goes into an attached context's `env.dateFormat` */
    dateFormat: string | null = null;

    private pending = new Map<string, PendingAttachment[]>();
    private asks = new Subject<string>();

    /** error texts the user clicked "Ask CoPilot" on */
    get askAboutError$(): Observable<string> {
        return this.asks.asObservable();
    }

    askAboutError(errorText: string): void {
        if (this.copilotEnabled) {
            this.asks.next(errorText || '');
        }
    }

    get(sessionKey: string): PendingAttachment[] {
        return this.pending.get(sessionKey) || [];
    }

    set(sessionKey: string, list: PendingAttachment[]): void {
        if (list && list.length) {
            this.pending.set(sessionKey, list);
        } else {
            this.pending.delete(sessionKey);
        }
    }

    /** Returns what is pending for the session and clears it (the message that carries it is being sent). */
    take(sessionKey: string): PendingAttachment[] {
        const list = this.get(sessionKey);
        this.pending.delete(sessionKey);
        return list;
    }

    clear(sessionKey: string): void {
        this.pending.delete(sessionKey);
    }
}
